/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, map } from 'rxjs';
import { HttpService, TranslationService, AuthorizationStore } from '@nebula-shared/data-access';
import { SidebarMenuItem } from '../../../domain/src/lib/sidebar.model';

@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly http = inject(HttpService);
  private readonly translation = inject(TranslationService);
  private readonly authorizationStore = inject(AuthorizationStore);
  private readonly MENU_ENDPOINT = '/simappe-admin/api/v1/menu';

  /**
   * Mapa segmento-de-ruta -> clave de traduccion (optionName/optionGroupName), independiente del
   * idioma. Se llena al construir el menu y permite re-traducir titulos de pestañas abiertas al
   * cambiar idioma (el {@link NebulaNavigationService} del kit expone `tabs` como señal read-only).
   */
  readonly routeKeyMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> clave del GRUPO de menu al que pertenece la opcion.
   *
   * El grupo no aparece en la ruta (`/layout/accounting/exchange-rate/list` no
   * lo nombra), pero el breadcrumb lo necesita para mostrar el camino completo.
   * Se llena del mismo recorrido del menu que llena {@link routeKeyMap}.
   */
  readonly routeGroupMap = new Map<string, string>();

  /**
   * Mapa segmento-de-ruta -> ICONO del GRUPO de menu al que pertenece la opcion.
   * Se llena del mismo recorrido del menu que llena {@link routeGroupMap}.
   */
  readonly routeGroupIconMap = new Map<string, string>();

  /** Claves de localStorage donde sobreviven los mapas entre recargas. */
  private static readonly ROUTE_KEYS_CACHE = 'nebula_route_keys';
  private static readonly ROUTE_GROUPS_CACHE = 'nebula_route_groups';
  private static readonly ROUTE_GROUPS_ICON_CACHE = 'nebula_route_groups_icon';

  constructor() {
    this.restoreRouteKeys();
    this.restoreRouteGroupIcons();
  }

  /** Segmentos de accion/ruta que NO identifican la opcion (se ignoran al derivar la clave). */
  private readonly ROUTE_ACTION_SEGMENTS = new Set([
    'list',
    'create',
    'edit',
    'view',
    'detail',
    'new',
    'layout',
  ]);

  /** Ultimo segmento significativo de una ruta (ignora acciones e ids). Ej: '/layout/accounting/tipo-plan/list' -> 'tipo-plan'. */
  routeKeySegment(route: string | undefined): string {
    if (!route) return '';
    const parts = route
      .split('/')
      .map((p) => p.trim())
      .filter(
        (p) =>
          p && !this.ROUTE_ACTION_SEGMENTS.has(p) && !/^\d+$/.test(p) && !/^[0-9a-f]{8}-/.test(p),
      );
    return parts.length ? parts[parts.length - 1] : '';
  }

  /** Clave de traduccion asociada al segmento significativo de una ruta (si esta mapeada). */
  keyForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeKeyMap.get(seg) : undefined;
  }

  /**
   * Clave del grupo de menu al que pertenece la opcion de `route`.
   *
   * Una opcion puede figurar en mas de un grupo; se responde el ultimo que la
   * declaro al recorrer el menu, que es un criterio estable mientras el menu
   * no cambie. Devuelve undefined para rutas que no son opciones de menu.
   */
  groupKeyForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeGroupMap.get(seg) : undefined;
  }

  /**
   * Icono del grupo de menu al que pertenece la opcion de `route`.
   */
  groupIconForRoute(route: string | undefined): string | undefined {
    const seg = this.routeKeySegment(route);
    return seg ? this.routeGroupIconMap.get(seg) : undefined;
  }

  getMenu(): Observable<SidebarMenuItem[]> {
    const userDataJson = localStorage.getItem('user_data');
    if (!userDataJson) return throwError(() => new Error('Sin sesión'));

    const selectedProductId = localStorage.getItem('selectedProductId');

    const userData = JSON.parse(userDataJson);
    const roleIds: number[] = userData.roles?.map((r: { id: string }) => r.id) || [];
    if (!roleIds.length) return throwError(() => new Error('Sin roles'));

    this.authorizationStore.load().subscribe();

    return this.http.post<any>(`${this.MENU_ENDPOINT}/read-by-roles-grouped`, roleIds).pipe(
      map((response: any) => {
        const content = response?.response?.content;

        const items = Array.isArray(content) ? content : [];

        if (!items.length) return [];

        const filteredItems = this.filterByApplicationId(items, selectedProductId);

        return this.buildMenuTree(filteredItems);
      }),
    );
  }

  private filterByApplicationId(items: any[], selectedProductId: string | null): any[] {
    if (!selectedProductId) {
      return items;
    }

    const filteredItems: any[] = [];

    items.forEach((role) => {
      if (!role.applications) return;

      const filteredApplications = role.applications.filter(
        (app: { applicationId: { toString: () => string } }) =>
          app.applicationId?.toString() === selectedProductId,
      );

      if (filteredApplications.length > 0) {
        filteredItems.push({
          ...role,
          applications: filteredApplications,
        });
      }
    });

    return filteredItems;
  }

  private buildMenuTree(items: any[]): SidebarMenuItem[] {
    const menuItems: SidebarMenuItem[] = [];
    const processedGroups = new Set<string>();

    items.forEach((role) => {
      if (!role.applications) return;

      role.applications.forEach((app: any) => {
        if (!app.modules) return;

        app.modules.forEach((module: any) => {
          if (!module.optionGroups?.length) return;

          module.optionGroups.forEach((group: any) => {
            const groupKey = `${group.optionGroupId}`;
            if (processedGroups.has(groupKey)) return;
            processedGroups.add(groupKey);

            const groupIcon = this.getIconName(group.optionGroupAhrefIconClass);

            const groupItem: SidebarMenuItem = {
              label: this.getLabel(group.optionGroupName),
              icon: groupIcon,
              route: group.optionGroupUrl,
              children: [],
              expanded: false,
            };
            this.registerRouteKey(group.optionGroupUrl, group.optionGroupName);

            if (group.options?.length) {
              group.options.forEach((opt: any) => {
                const optRoute = opt.optionFullPath || opt.optionPath;
                groupItem.children!.push({
                  label: this.getLabel(opt.optionName),
                  icon: this.getIconName(opt.optionAhrefIconClass),
                  route: optRoute,
                  children: [],
                });
                this.registerRouteKey(optRoute, opt.optionName);
                this.registerRouteGroup(optRoute, group.optionGroupName);
                this.registerRouteGroupIcon(optRoute, groupIcon);
              });
            }

            menuItems.push(groupItem);
          });
        });
      });
    });

    this.persistRouteKeys();
    this.persistRouteGroupIcons();
    return menuItems;
  }

  private getIconName(iconValue: string | undefined): string {
    if (!iconValue) return 'Folder';
    return iconValue;
  }

  /** Asocia el segmento significativo de una ruta con su clave de traduccion (optionName/group). */
  private registerRouteKey(route: string | undefined, key: string | undefined): void {
    if (!key) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeKeyMap.set(seg, key);
  }

  /** Asocia el segmento significativo de una ruta con la clave del grupo que la contiene. */
  private registerRouteGroup(route: string | undefined, groupKey: string | undefined): void {
    if (!groupKey) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeGroupMap.set(seg, groupKey);
  }

  /** Asocia el segmento significativo de una ruta con el icono del grupo que la contiene. */
  private registerRouteGroupIcon(route: string | undefined, icon: string | undefined): void {
    if (!icon) return;
    const seg = this.routeKeySegment(route);
    if (seg) this.routeGroupIconMap.set(seg, icon);
  }

  /** Rehidrata el mapa de iconos de grupos. */
  private restoreRouteGroupIcons(): void {
    this.restoreMap(SidebarService.ROUTE_GROUPS_ICON_CACHE, this.routeGroupIconMap);
  }

  /** Persiste el mapa de iconos de grupos. */
  private persistRouteGroupIcons(): void {
    this.persistMap(SidebarService.ROUTE_GROUPS_ICON_CACHE, this.routeGroupIconMap);
  }

  private getLabel(key: string | undefined): string {
    if (!key) return '';
    const translated = this.translation.translate(key);
    if (translated && translated !== key) return translated;
    return this.formatKey(key);
  }

  /**
   * Etiqueta de un segmento de ruta, para el breadcrumb.
   *
   * Resuelve en tres pasos, todos contra el bundle:
   *  1. la clave de la opcion de menu registrada para ese segmento, que es la
   *     misma etiqueta que muestra el sidebar (asi no hacen falta claves
   *     paralelas para lo que el menu ya nombra);
   *  2. `BC.<segmento>` para lo que no es una opcion (modulos y acciones como
   *     list/create/edit);
   *  3. el segmento formateado, cuando ninguna de las dos esta traducida.
   */
  labelForSegment(segment: string): string {
    if (!segment) return '';

    const optionKey = this.routeKeyMap.get(segment);
    if (optionKey) {
      const porMenu = this.translation.translate(optionKey);
      if (porMenu && porMenu !== optionKey) return porMenu;
    }

    if (/^\d+$/.test(segment) || /^[0-9a-f]{8}-/.test(segment)) {
      return this.translateOr('BC.detail', 'Detalle');
    }

    return this.translateOr(`BC.${segment}`, this.formatSegment(segment));
  }

  /** Traduccion de `key`, o `alterno` si esa clave no esta en el bundle. */
  private translateOr(key: string, alterno: string): string {
    const value = this.translation.translate(key);
    return value !== key ? value : alterno;
  }

  private formatKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /** 'accounting-currency' -> 'Accounting Currency'. */
  private formatSegment(segment: string): string {
    return segment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /** Rehidrata los mapas de segmentos para que el breadcrumb no dependa de que el menu ya haya cargado. */
  private restoreRouteKeys(): void {
    this.restoreMap(SidebarService.ROUTE_KEYS_CACHE, this.routeKeyMap);
    this.restoreMap(SidebarService.ROUTE_GROUPS_CACHE, this.routeGroupMap);
  }

  private restoreMap(cacheKey: string, destino: Map<string, string>): void {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const entries = JSON.parse(raw) as [string, string][];
      entries.forEach(([seg, key]) => destino.set(seg, key));
    } catch {
      /* cache ilegible: se repuebla al cargar el menu */
    }
  }

  private persistRouteKeys(): void {
    this.persistMap(SidebarService.ROUTE_KEYS_CACHE, this.routeKeyMap);
    this.persistMap(SidebarService.ROUTE_GROUPS_CACHE, this.routeGroupMap);
  }

  private persistMap(cacheKey: string, origen: Map<string, string>): void {
    try {
      localStorage.setItem(cacheKey, JSON.stringify([...origen.entries()]));
    } catch {
      /* cuota de localStorage: el mapa sigue vivo en memoria */
    }
  }
}
